/**
 * YPlanValve — animated SVG showing a 3-port mid-position valve.
 * Three positions: heating only, hot water only, mid-position (both).
 */

import { useState, useEffect } from 'react';

export function YPlanValve() {
  const [position, setPosition] = useState<'hw' | 'mid' | 'ch'>('hw');

  useEffect(() => {
    const sequence: Array<'hw' | 'mid' | 'ch'> = ['hw', 'mid', 'ch', 'mid'];
    let idx = 0;
    const timer = setInterval(() => {
      idx = (idx + 1) % sequence.length;
      setPosition(sequence[idx]);
    }, 2000);
    return () => clearInterval(timer);
  }, []);

  const portAngle = position === 'hw' ? -45 : position === 'ch' ? 45 : 0;

  return (
    <figure style={{ margin: 0 }}>
      <svg viewBox="0 0 400 260" width="100%" role="img" aria-label="Y-plan 3-port valve animation">
        <title>Y-plan 3-port mid-position valve</title>
        <defs>
          <style>{`
            .valve-body { fill: #e2e8f0; stroke: #475569; stroke-width: 2; }
            .valve-port { fill: #94a3b8; stroke: #334155; stroke-width: 1.5; }
            .valve-arm { stroke: #2563eb; stroke-width: 4; stroke-linecap: round; transition: transform 0.6s ease-in-out; }
            .pipe-line { stroke: #64748b; stroke-width: 3; fill: none; }
            .pipe-flow { stroke: #3b82f6; stroke-width: 3; fill: none; opacity: 0.6; stroke-dasharray: 8,6; }
            .valve-label { font-family: system-ui, sans-serif; font-size: 12px; fill: #475569; text-anchor: middle; }
            .valve-title { font-family: system-ui, sans-serif; font-size: 13px; fill: #1e293b; font-weight: 600; text-anchor: middle; }
            .flow-anim { animation: flow-dash 1s linear infinite; }
            @keyframes flow-dash { to { stroke-dashoffset: -14; } }
          `}</style>
        </defs>

        {/* Pipes */}
        <line x1="200" y1="20" x2="200" y2="80" className="pipe-line" />
        <line x1="100" y1="200" x2="160" y2="140" className="pipe-line" />
        <line x1="300" y1="200" x2="240" y2="140" className="pipe-line" />

        {/* Flow indicators */}
        {(position === 'hw' || position === 'mid') && (
          <line x1="100" y1="200" x2="160" y2="140" className="pipe-flow flow-anim" />
        )}
        {(position === 'ch' || position === 'mid') && (
          <line x1="300" y1="200" x2="240" y2="140" className="pipe-flow flow-anim" />
        )}
        <line x1="200" y1="20" x2="200" y2="80" className="pipe-flow flow-anim" />

        {/* Valve body */}
        <circle cx="200" cy="120" r="40" className="valve-body" />

        {/* Ports */}
        <circle cx="200" cy="80" r="6" className="valve-port" />
        <circle cx="160" cy="140" r="6" className="valve-port" />
        <circle cx="240" cy="140" r="6" className="valve-port" />

        {/* Rotating arm */}
        <g transform={`rotate(${portAngle}, 200, 120)`}>
          <line x1="200" y1="120" x2="200" y2="82" className="valve-arm" />
          <circle cx="200" cy="120" r="5" fill="#2563eb" />
        </g>

        {/* Labels */}
        <text x="200" y="15" className="valve-label">Boiler flow</text>
        <text x="80" y="220" className="valve-label">Hot water</text>
        <text x="320" y="220" className="valve-label">Heating</text>

        {/* Position label */}
        <text x="200" y="250" className="valve-title">
          {position === 'hw' ? 'Hot water only' : position === 'ch' ? 'Heating only' : 'Mid-position (both)'}
        </text>
      </svg>
    </figure>
  );
}
